const WINDOW_DAYS = { '3M': 93, '6M': 186, '1Y': 366 };

function finite(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

export function sma(values, period) {
    const out = Array(values.length).fill(null);
    if (!Number.isInteger(period) || period < 1) return out;
    const window = [];
    let sum = 0;
    let count = 0;
    for (let i = 0; i < values.length; i += 1) {
        const v = finite(values[i]);
        window.push(v);
        if (v != null) {
            sum += v;
            count += 1;
        }
        if (window.length > period) {
            const removed = window.shift();
            if (removed != null) {
                sum -= removed;
                count -= 1;
            }
        }
        if (window.length === period && count === period) out[i] = sum / period;
    }
    return out;
}

export function ema(values, period) {
    const out = Array(values.length).fill(null);
    if (!Number.isInteger(period) || period < 1) return out;
    const k = 2 / (period + 1);
    let prev = null;
    let seed = [];
    let seedSum = 0;
    for (let i = 0; i < values.length; i += 1) {
        const v = finite(values[i]);
        if (v == null) {
            if (prev == null) {
                seed = [];
                seedSum = 0;
            }
            continue;
        }
        if (prev == null) {
            seed.push(v);
            seedSum += v;
            if (seed.length === period) {
                prev = seedSum / period;
                out[i] = prev;
            }
            continue;
        }
        prev = (v * k) + (prev * (1 - k));
        out[i] = prev;
    }
    return out;
}

function rsiFromAverages(avgGain, avgLoss) {
    if (avgGain === 0 && avgLoss === 0) return null;
    if (avgLoss === 0) return 100;
    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
}

export function rsi(closes, period = 14) {
    const out = Array(closes.length).fill(null);
    if (!Number.isInteger(period) || period < 1) return out;
    let gainSum = 0;
    let lossSum = 0;
    let changes = 0;
    let avgGain = null;
    let avgLoss = null;
    for (let i = 1; i < closes.length; i += 1) {
        const prev = finite(closes[i - 1]);
        const cur = finite(closes[i]);
        if (prev == null || cur == null) {
            if (avgGain == null) {
                gainSum = 0;
                lossSum = 0;
                changes = 0;
            }
            continue;
        }
        const change = cur - prev;
        const gain = change > 0 ? change : 0;
        const loss = change < 0 ? -change : 0;
        if (avgGain == null) {
            gainSum += gain;
            lossSum += loss;
            changes += 1;
            if (changes === period) {
                avgGain = gainSum / period;
                avgLoss = lossSum / period;
                out[i] = rsiFromAverages(avgGain, avgLoss);
            }
            continue;
        }
        avgGain = ((avgGain * (period - 1)) + gain) / period;
        avgLoss = ((avgLoss * (period - 1)) + loss) / period;
        out[i] = rsiFromAverages(avgGain, avgLoss);
    }
    return out;
}

export function atr(bars, period = 14) {
    const out = Array(bars.length).fill(null);
    if (!Number.isInteger(period) || period < 1) return out;
    let prevClose = null;
    let seed = [];
    let seedSum = 0;
    let value = null;
    for (let i = 0; i < bars.length; i += 1) {
        const high = finite(bars[i]?.high) ?? finite(bars[i]?.close);
        const low = finite(bars[i]?.low) ?? finite(bars[i]?.close);
        const close = finite(bars[i]?.close);
        if (high == null || low == null || close == null || prevClose == null) {
            if (close != null) prevClose = close;
            continue;
        }
        const tr = Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose));
        prevClose = close;
        if (value == null) {
            seed.push(tr);
            seedSum += tr;
            if (seed.length === period) {
                value = seedSum / period;
                out[i] = value;
            }
            continue;
        }
        value = ((value * (period - 1)) + tr) / period;
        out[i] = value;
    }
    return out;
}

export function bollingerBands(closes, period = 20, multiplier = 2) {
    const out = Array(closes.length).fill({ upper: null, middle: null, lower: null });
    if (!Number.isInteger(period) || period < 1) return out;
    
    const middle = sma(closes, period);
    
    for (let i = period - 1; i < closes.length; i += 1) {
        if (middle[i] == null) continue;
        
        let sumSq = 0;
        let count = 0;
        for (let j = 0; j < period; j++) {
            const val = finite(closes[i - j]);
            if (val != null) {
                sumSq += Math.pow(val - middle[i], 2);
                count++;
            }
        }
        
        if (count === period) {
            const stdDev = Math.sqrt(sumSq / period);
            out[i] = {
                upper: middle[i] + (multiplier * stdDev),
                middle: middle[i],
                lower: middle[i] - (multiplier * stdDev)
            };
        }
    }
    return out;
}

export function supertrend(bars, period = 10, multiplier = 3) {
    const out = Array(bars.length).fill({ value: null, trend: null });
    if (!Number.isInteger(period) || period < 1) return out;
    
    const atrVals = atr(bars, period);
    let finalUpperband = null;
    let finalLowerband = null;
    let supertrendVal = null;
    let trend = 1; // 1 for uptrend, -1 for downtrend
    
    for (let i = 0; i < bars.length; i++) {
        const high = finite(bars[i]?.high) ?? finite(bars[i]?.close);
        const low = finite(bars[i]?.low) ?? finite(bars[i]?.close);
        const close = finite(bars[i]?.close);
        const prevClose = i > 0 ? finite(bars[i-1]?.close) : null;
        
        if (high == null || low == null || close == null || atrVals[i] == null) {
            continue;
        }
        
        const basicUpperband = ((high + low) / 2) + (multiplier * atrVals[i]);
        const basicLowerband = ((high + low) / 2) - (multiplier * atrVals[i]);
        
        if (finalUpperband == null) {
            finalUpperband = basicUpperband;
            finalLowerband = basicLowerband;
            continue;
        }
        
        if (basicUpperband < finalUpperband || (prevClose != null && prevClose > finalUpperband)) {
            finalUpperband = basicUpperband;
        }
        
        if (basicLowerband > finalLowerband || (prevClose != null && prevClose < finalLowerband)) {
            finalLowerband = basicLowerband;
        }
        
        let prevTrend = trend;
        if (supertrendVal == null) {
            if (close <= finalUpperband) trend = -1;
            else trend = 1;
        } else if (prevTrend === 1 && close < finalLowerband) {
            trend = -1;
        } else if (prevTrend === -1 && close > finalUpperband) {
            trend = 1;
        }
        
        if (trend === 1) {
            supertrendVal = finalLowerband;
        } else {
            supertrendVal = finalUpperband;
        }
        
        out[i] = { value: supertrendVal, trend };
    }
    
    return out;
}

export function macd(closes, fast = 12, slow = 26, signalPeriod = 9) {
    const fastEma = ema(closes, fast);
    const slowEma = ema(closes, slow);
    const line = closes.map((_, i) => (
        fastEma[i] != null && slowEma[i] != null ? fastEma[i] - slowEma[i] : null
    ));
    const signal = ema(line, signalPeriod);
    const histogram = line.map((value, i) => (
        value != null && signal[i] != null ? value - signal[i] : null
    ));
    return { line, signal, histogram };
}

function fridayKey(dateStr) {
    const day = String(dateStr || '').slice(0, 10);
    const date = new Date(`${day}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return null;
    const add = (5 - date.getUTCDay() + 7) % 7;
    date.setUTCDate(date.getUTCDate() + add);
    return date.toISOString().slice(0, 10);
}

export function toWeeklyBars(daily) {
    const groups = new Map();
    (Array.isArray(daily) ? daily : []).forEach((bar) => {
        const key = fridayKey(bar?.date);
        const close = finite(bar?.close);
        if (!key || close == null) return;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(bar);
    });
    return Array.from(groups.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, bars]) => {
            const open = finite(bars[0].open) ?? finite(bars[0].close);
            const close = finite(bars[bars.length - 1].close);
            const highs = bars.map((bar) => finite(bar.high) ?? finite(bar.close)).filter((v) => v != null);
            const lows = bars.map((bar) => finite(bar.low) ?? finite(bar.close)).filter((v) => v != null);
            const volumes = bars.map((bar) => finite(bar.volume)).filter((v) => v != null);
            return {
                date,
                open,
                high: highs.length ? Math.max(...highs) : close,
                low: lows.length ? Math.min(...lows) : close,
                close,
                volume: volumes.length ? volumes.reduce((sum, v) => sum + v, 0) : null,
            };
        });
}

export function enrichBars(bars) {
    const source = Array.isArray(bars) ? bars : [];
    const closes = source.map((bar) => finite(bar?.close));
    const volumes = source.map((bar) => finite(bar?.volume));
    const ema20 = ema(closes, 20);
    const sma50 = sma(closes, 50);
    const sma200 = sma(closes, 200);
    const rsi14 = rsi(closes, 14);
    const macdResult = macd(closes);
    const atr14 = atr(source, 14);
    const volumeAvg20 = sma(volumes, 20);
    
    // Swing trading additions
    const bb20 = bollingerBands(closes, 20, 2);
    const st10 = supertrend(source, 10, 3);
    
    const byDate = new Map();
    source.forEach((bar, i) => {
        const close = closes[i];
        const date = String(bar?.date || '').slice(0, 10);
        if (close == null || !date) return;
        byDate.set(date, {
            date,
            open: finite(bar.open) ?? close,
            high: finite(bar.high) ?? close,
            low: finite(bar.low) ?? close,
            close,
            volume: volumes[i],
            ema20: ema20[i],
            sma50: sma50[i],
            sma200: sma200[i],
            rsi14: rsi14[i],
            macdLine: macdResult.line[i],
            macdSignal: macdResult.signal[i],
            macdHist: macdResult.histogram[i],
            atr14: atr14[i],
            volumeAvg20: volumeAvg20[i],
            bbUpper: bb20[i]?.upper,
            bbMiddle: bb20[i]?.middle,
            bbLower: bb20[i]?.lower,
            stValue: st10[i]?.value,
            stTrend: st10[i]?.trend,
        });
    });
    return Array.from(byDate.values());
}

export function sliceWindow(rows, period) {
    if (!Array.isArray(rows) || !rows.length || period === '1Y' || !WINDOW_DAYS[period]) return rows || [];
    const end = new Date(`${rows[rows.length - 1].date}T00:00:00Z`);
    if (Number.isNaN(end.getTime())) return rows;
    const start = new Date(end);
    start.setUTCDate(start.getUTCDate() - WINDOW_DAYS[period]);
    const startKey = start.toISOString().slice(0, 10);
    return rows.filter((row) => row.date >= startKey);
}
